import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listUsers, updateUserModeration } from "@/lib/admin";
import { fmtError } from "@/lib/supabase";
import Avatar from "@/components/Avatar";
import ConfirmModal from "@/components/ConfirmModal";
import {
  Search, ShieldCheck, ShieldOff, Trash2, Pause, Play, BadgeCheck, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listUsers({ q, page, pageSize: PAGE_SIZE })
      .then((r) => { setRows(r.rows); setTotal(r.total); })
      .catch((e) => toast.error(fmtError(e)))
      .finally(() => setLoading(false));
  }, [q, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const apply = async (user, patch, label) => {
    try {
      await updateUserModeration(user.id, patch);
      toast.success(label);
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const toggleSuspend = (u) => setConfirm({
    title: u.is_suspended ? "Re-enable user?" : "Suspend user?",
    body: u.is_suspended ? "They'll regain full access." : "They won't be able to post or join events.",
    confirmLabel: u.is_suspended ? "Re-enable" : "Suspend",
    danger: !u.is_suspended,
    onConfirm: () => apply(u, { is_suspended: !u.is_suspended }, u.is_suspended ? "Re-enabled." : "Suspended."),
  });

  const toggleVerified = (u) => apply(
    u, { is_verified_admin: !u.is_verified_admin },
    u.is_verified_admin ? "Verified badge removed." : "Verified."
  );

  const softDelete = (u) => setConfirm({
    title: "Soft-delete user?",
    body: "Their profile and content will be hidden from the app. This is reversible from SQL.",
    confirmLabel: "Soft delete",
    danger: true,
    onConfirm: () => apply(u, { is_deleted: true }, "User soft-deleted."),
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-4">
      <div className="relative max-w-md">
        <Search size={16} strokeWidth={2.2} className="absolute left-5 top-1/2 -translate-y-1/2 text-ink/45 z-10" />
        <input
          value={q}
          onChange={(e) => { setPage(0); setQ(e.target.value); }}
          placeholder="Search by name, username…"
          data-testid="admin-users-search"
          className="chunky-input rounded-full pl-12"
        />
      </div>

      <div className="pop p-0 overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-cream border-b-[2.5px] border-ink">
              <tr className="text-left text-[10px] font-extrabold uppercase tracking-widest text-ink/65">
                <th className="px-4 py-3">User</th>
                <th className="px-2 py-3">Hosted</th>
                <th className="px-2 py-3">Status</th>
                <th className="px-2 py-3">Joined</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-ink/55">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-ink/55">No users.</td></tr>
              ) : rows.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-ink/10 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar src={u.avatar_url} name={u.full_name || u.display_name} size="sm" />
                      <div className="min-w-0">
                        <p className="font-bold text-ink truncate flex items-center gap-1.5">
                          {u.full_name || u.display_name || "anon"}
                          {u.is_verified_admin && <BadgeCheck size={13} strokeWidth={2.5} className="text-ink fill-lime" />}
                        </p>
                        <p className="text-ink/55 text-xs truncate">
                          @{u.username || "—"}
                          {u.role === "admin" && <span className="ml-2 inline-block bg-lime text-ink border-2 border-ink rounded-full px-1.5 py-0 text-[9px] font-extrabold uppercase">Admin</span>}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3 text-ink/70">{u.hosted_count}</td>
                  <td className="px-2 py-3">
                    {u.is_deleted ? <Tag>Deleted</Tag>
                    : u.is_suspended ? <Tag>Suspended</Tag>
                    : <Tag tone="lime">Active</Tag>}
                  </td>
                  <td className="px-2 py-3 text-ink/65 whitespace-nowrap">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <RowActions
                      user={u}
                      onSuspend={() => toggleSuspend(u)}
                      onVerify={() => toggleVerified(u)}
                      onDelete={() => softDelete(u)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile list */}
        <div className="md:hidden divide-y-2 divide-ink/10">
          {rows.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center gap-3 p-3">
              <Avatar src={u.avatar_url} name={u.full_name || u.display_name} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-ink truncate text-sm flex items-center gap-1">
                  {u.full_name || u.display_name || "anon"}
                  {u.is_verified_admin && <BadgeCheck size={12} strokeWidth={2.5} className="fill-lime" />}
                </p>
                <p className="text-ink/55 text-[11px] truncate">@{u.username || "—"} · {u.hosted_count} hosted</p>
              </div>
              <RowActions
                compact
                user={u}
                onSuspend={() => toggleSuspend(u)}
                onVerify={() => toggleVerified(u)}
                onDelete={() => softDelete(u)}
              />
            </div>
          ))}
        </div>
      </div>

      <Pager page={page} totalPages={totalPages} onChange={setPage} />

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel || "Confirm"}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { await confirm?.onConfirm?.(); setConfirm(null); }}
      />
    </div>
  );
}

function RowActions({ user, onSuspend, onVerify, onDelete, compact = false }) {
  const btn = "inline-flex items-center justify-center w-9 h-9 border-[2.5px] border-ink rounded-full bg-paper hover:bg-lime transition-colors";
  return (
    <div className={`flex ${compact ? "gap-1.5" : "justify-end gap-2"}`}>
      <button title={user.is_verified_admin ? "Unverify" : "Verify"} data-testid={`btn-verify-${user.id}`}
        onClick={onVerify} className={btn}>
        {user.is_verified_admin ? <ShieldOff size={14} strokeWidth={2.2} /> : <ShieldCheck size={14} strokeWidth={2.2} />}
      </button>
      <button title={user.is_suspended ? "Re-enable" : "Suspend"} data-testid={`btn-suspend-${user.id}`}
        onClick={onSuspend} className={btn}>
        {user.is_suspended ? <Play size={14} strokeWidth={2.2} /> : <Pause size={14} strokeWidth={2.2} />}
      </button>
      <button title="Soft delete" data-testid={`btn-delete-${user.id}`}
        onClick={onDelete} className={btn + " hover:!bg-destructive hover:!text-paper"}>
        <Trash2 size={14} strokeWidth={2.2} />
      </button>
    </div>
  );
}

function Tag({ children, tone = "neutral" }) {
  const klass = tone === "lime" ? "bg-lime text-ink" : "bg-paper text-ink/70";
  return (
    <span className={`inline-flex items-center ${klass} border-[2px] border-ink rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-widest`}>
      {children}
    </span>
  );
}

function Pager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3">
      <button disabled={page === 0} onClick={() => onChange(page - 1)}
        className="inline-flex items-center justify-center w-10 h-10 border-[2.5px] border-ink rounded-full bg-paper hover:bg-lime disabled:opacity-40 transition-colors">
        <ChevronLeft size={16} strokeWidth={2.5} />
      </button>
      <span className="text-sm font-bold text-ink/70">Page {page + 1} / {totalPages}</span>
      <button disabled={page >= totalPages - 1} onClick={() => onChange(page + 1)}
        className="inline-flex items-center justify-center w-10 h-10 border-[2.5px] border-ink rounded-full bg-paper hover:bg-lime disabled:opacity-40 transition-colors">
        <ChevronRight size={16} strokeWidth={2.5} />
      </button>
    </div>
  );
}
